import { useBusinessEntity, useJurisdictions } from '@commenda/api';
import { useDirectorsByCorporation } from '@commenda/api/director';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@commenda/kakashi';
import {
  CorporationType,
  Country,
  LegacyDocumentType,
} from '@commenda/prisma';
import { BusinessLocationType } from '@commenda/types';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import {
  getCorporationName,
  getPrintableCorporationType,
} from '@commenda/utils';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';
import { Country as CountryUtil, State } from 'country-state-city';
import { getDirectorName } from '../Director/common';
import { DocumentSummaryField } from './DocumentSummaryField';
import { SummaryFieldKS } from './SummaryFieldKS';

interface ICorporateShareholder {
  id: number;
  companyLegalName?: string;
  entityEmail?: string;
  corporationType: CorporationType;
  incorporationCountry: Country;
  /** The content's jurisdiction id: a corporate shareholder is an entity. */
  jurisdiction: string;
}

type TCorporateShareholdersSummaryProps = {
  corporateShareholders: ICorporateShareholder[];
  directorsData?: DirectorBase[];
};

const CorporateShareholderSummaryField = ({
  corporationId,
  directorsData,
}: {
  corporationId: number;
  directorsData?: DirectorBase[];
}) => {
  const { data: businessEntity } = useBusinessEntity(corporationId);
  const { data: corporationDirectors } = useDirectorsByCorporation(
    corporationId,
    ''
  );

  let authorizedSignatory = null;
  let signatoryRel = null;

  if (directorsData && directorsData.length > 0) {
    authorizedSignatory = directorsData.find((director) => {
      return director.corporationHasDirector?.some(
        (chd) =>
          chd.corporationId === corporationId &&
          chd.additionalDirectorInformation?.isAuthorizedSignatory
      );
    });

    if (authorizedSignatory) {
      signatoryRel = authorizedSignatory.corporationHasDirector?.find(
        (chd) =>
          chd.corporationId === corporationId &&
          chd.additionalDirectorInformation?.isAuthorizedSignatory
      );
    }
  }

  if (!authorizedSignatory && corporationDirectors) {
    authorizedSignatory = corporationDirectors.find(
      (director) =>
        director.additionalDirectorInformation?.isAuthorizedSignatory
    );
    // Each row carries its relation to this entity as well as the person.
    signatoryRel = authorizedSignatory;
  }

  const jobTitle =
    signatoryRel?.additionalDirectorInformation?.jobTitle?.[0] ||
    signatoryRel?.incorporationInfo?.jobTitle;

  const hasUsedDifferentName =
    signatoryRel?.incorporationInfo?.hasUsedDifferentName;

  const signatoryAddress = authorizedSignatory?.operatingAddress
    ? [
        authorizedSignatory.operatingAddress.addressLine1,
        authorizedSignatory.operatingAddress.addressLine2,
        `${authorizedSignatory.operatingAddress.city}, ${authorizedSignatory.operatingAddress.state}`,
        authorizedSignatory.operatingAddress.postalCode,
        authorizedSignatory.operatingAddress.country,
      ]
        .filter(Boolean)
        .join(', ')
    : undefined;

  const signatoryPhoneNumber = authorizedSignatory?.number;

  const operatingAddress = businessEntity?.locationsInfo?.locations?.find(
    (location) =>
      location.type.includes(BusinessLocationType.OPERATIONS) &&
      location.isPrimary
  )?.address;

  const principalPlaceOfBusiness = operatingAddress
    ? [
        operatingAddress.addressLine1,
        operatingAddress.addressLine2,
        `${operatingAddress.city}, ${operatingAddress.state}`,
        operatingAddress.postalCode,
        operatingAddress.country,
      ]
        .filter(Boolean)
        .join(', ')
    : undefined;

  const personalDetails = authorizedSignatory?.personalDetails;
  const placeOfBirth = personalDetails?.placeOfBirth;

  const formattedPlaceOfBirth = placeOfBirth
    ? [
        placeOfBirth.district,
        State.getStateByCodeAndCountry(placeOfBirth.state, placeOfBirth.country)
          ?.name || placeOfBirth.state,
        CountryUtil.getCountryByCode(placeOfBirth.country)?.name ||
          placeOfBirth.country,
      ]
        .filter(Boolean)
        .join(', ')
    : undefined;

  const passportFiles =
    authorizedSignatory?.directorFiles?.filter(
      (file) => file.documentType === LegacyDocumentType.Passport
    ) ?? [];

  const passportFile =
    passportFiles.length > 0 ? passportFiles[passportFiles.length - 1] : null;

  const governmentIdFile = authorizedSignatory?.directorFiles?.find(
    (file) => file.documentType === LegacyDocumentType.GovernmentId
  );

  const addressProofFile =
    authorizedSignatory?.addressProof ||
    authorizedSignatory?.directorFiles?.find(
      (file) => file.documentType === LegacyDocumentType.AddressProof
    );

  const panCardFile =
    authorizedSignatory?.panCard ||
    authorizedSignatory?.directorFiles?.find(
      (file) => file.documentType === LegacyDocumentType.PanCard
    );

  const aadharCardFile =
    authorizedSignatory?.aadharCard ||
    authorizedSignatory?.directorFiles?.find(
      (file) => file.documentType === LegacyDocumentType.AadharCard
    );

  const passportSizePhotoFile = authorizedSignatory?.directorFiles?.find(
    (file) => file.documentType === LegacyDocumentType.PassportSizePhoto
  );

  const specimenSignatureFile = authorizedSignatory?.directorFiles?.find(
    (file) => file.documentType === LegacyDocumentType.SpecimenSignature
  );

  const drivingLicenseFile = authorizedSignatory?.directorFiles?.find(
    (file) => file.documentType === LegacyDocumentType.DrivingLicense
  );

  const voterIdFile = authorizedSignatory?.directorFiles?.find(
    (file) => file.documentType === LegacyDocumentType.VoterId
  );

  const openFile = async (fileId: number) => {
    const res = await getSignedUrlByFileId(fileId, null);
    window.open(res);
  };

  return (
    <>
      {principalPlaceOfBusiness && (
        <SummaryFieldKS
          heading="Corporate Stakeholder Address"
          value={principalPlaceOfBusiness}
        />
      )}

      {authorizedSignatory && (
        <>
          <SummaryFieldKS
            heading="Authorized Signatory"
            value={getDirectorName(authorizedSignatory)}
          />

          <SummaryFieldKS heading="Email" value={authorizedSignatory.email} />
          <SummaryFieldKS
            heading="Phone Number"
            value={authorizedSignatory.number || '-'}
          />
          {authorizedSignatory.dateOfBirth && (
            <SummaryFieldKS
              heading="Date of Birth"
              value={authorizedSignatory.dateOfBirth}
            />
          )}
          {authorizedSignatory.directorMiddleName && (
            <SummaryFieldKS
              heading="Middle Name"
              value={authorizedSignatory.directorMiddleName}
            />
          )}

          {jobTitle && <SummaryFieldKS heading="Job Title" value={jobTitle} />}
          {authorizedSignatory.jobTitle && (
            <SummaryFieldKS
              heading="Position"
              value={authorizedSignatory.jobTitle}
            />
          )}

          {signatoryAddress && (
            <SummaryFieldKS
              heading="Authorized Signatory Address"
              value={signatoryAddress}
            />
          )}

          {personalDetails?.nationality && (
            <SummaryFieldKS
              heading="Nationality"
              value={personalDetails.nationality}
            />
          )}
          {personalDetails?.residence && (
            <SummaryFieldKS
              heading="Residence"
              value={personalDetails.residence}
            />
          )}
          {personalDetails?.occupation && (
            <SummaryFieldKS
              heading="Occupation"
              value={personalDetails.occupation}
            />
          )}
          {personalDetails?.educationalQualification && (
            <SummaryFieldKS
              heading="Educational Qualification"
              value={personalDetails.educationalQualification}
            />
          )}
          {personalDetails?.gender && (
            <SummaryFieldKS heading="Gender" value={personalDetails.gender} />
          )}
          {personalDetails?.parentName && (
            <SummaryFieldKS
              heading="Parent Name"
              value={personalDetails.parentName}
            />
          )}
          {personalDetails?.taxPayerIdentificationNumber && (
            <SummaryFieldKS
              heading="Tax Payer Identification Number"
              value={personalDetails.taxPayerIdentificationNumber}
            />
          )}
          {personalDetails?.dateOfOccupancyInCurrentAddress && (
            <SummaryFieldKS
              heading="Date of Occupancy in Current Address"
              value={personalDetails.dateOfOccupancyInCurrentAddress}
            />
          )}

          {formattedPlaceOfBirth && (
            <SummaryFieldKS
              heading="Place of Birth"
              value={formattedPlaceOfBirth}
            />
          )}

          {personalDetails?.passportNumber && (
            <SummaryFieldKS
              heading="Passport Number"
              value={personalDetails.passportNumber}
            />
          )}
          {personalDetails?.passportCountryOfIssue && (
            <SummaryFieldKS
              heading="Passport Country of Issue"
              value={personalDetails.passportCountryOfIssue}
            />
          )}
          {personalDetails?.passportExpirationDate && (
            <SummaryFieldKS
              heading="Passport Expiration Date"
              value={personalDetails.passportExpirationDate}
            />
          )}

          {personalDetails?.panCardNumber && (
            <SummaryFieldKS
              heading="PAN Card Number"
              value={personalDetails.panCardNumber}
            />
          )}
          {personalDetails?.aadharCardNumber && (
            <SummaryFieldKS
              heading="Aadhar Card Number"
              value={personalDetails.aadharCardNumber}
            />
          )}
          {personalDetails?.hasDSC !== undefined && (
            <SummaryFieldKS
              heading="Has Digital Signature Certificate"
              value={personalDetails.hasDSC ? 'Yes' : 'No'}
            />
          )}

          {authorizedSignatory.directorIdentificationNumber && (
            <SummaryFieldKS
              heading="Director Identification Number"
              value={authorizedSignatory.directorIdentificationNumber}
            />
          )}

          {personalDetails?.isPoliticallyExposed !== undefined && (
            <SummaryFieldKS
              heading="Politically Exposed Person"
              value={personalDetails.isPoliticallyExposed ? 'Yes' : 'No'}
            />
          )}
          {personalDetails?.haveUAEResidenceVisa !== undefined && (
            <SummaryFieldKS
              heading="UAE Residence Visa"
              value={personalDetails.haveUAEResidenceVisa ? 'Yes' : 'No'}
            />
          )}

          {hasUsedDifferentName !== null &&
            hasUsedDifferentName !== undefined && (
              <SummaryFieldKS
                heading="Used different name for business purposes in last 20 years"
                value={hasUsedDifferentName ? 'Yes' : 'No'}
              />
            )}

          {passportFile?.fileUrl ? (
            <div className="mt-2">
              <DocumentSummaryField
                onClick={() => openFile(passportFile?.id)}
                title="Passport"
                className="grid grid-cols-2"
              />
            </div>
          ) : null}
          {governmentIdFile?.fileUrl ? (
            <div className="mt-2">
              <DocumentSummaryField
                onClick={() => openFile(governmentIdFile?.id)}
                title="Government ID"
                className="grid grid-cols-2"
              />
            </div>
          ) : null}
          {addressProofFile?.fileUrl ? (
            <div className="mt-2">
              <DocumentSummaryField
                onClick={() => openFile(addressProofFile?.id)}
                title="Address Proof"
                className="grid grid-cols-2"
              />
            </div>
          ) : null}
          {panCardFile?.fileUrl ? (
            <div className="mt-2">
              <DocumentSummaryField
                onClick={() => openFile(panCardFile?.id)}
                title="PAN Card"
                className="grid grid-cols-2"
              />
            </div>
          ) : null}
          {aadharCardFile?.fileUrl ? (
            <div className="mt-2">
              <DocumentSummaryField
                onClick={() => openFile(aadharCardFile?.id)}
                title="Aadhar Card"
                className="grid grid-cols-2"
              />
            </div>
          ) : null}
          {passportSizePhotoFile?.fileUrl ? (
            <div className="mt-2">
              <DocumentSummaryField
                onClick={() => openFile(passportSizePhotoFile?.id)}
                title="Passport Size Photo"
                className="grid grid-cols-2"
              />
            </div>
          ) : null}
          {specimenSignatureFile?.fileUrl ? (
            <div className="mt-2">
              <DocumentSummaryField
                onClick={() => openFile(specimenSignatureFile?.id)}
                title="Specimen Signature"
                className="grid grid-cols-2"
              />
            </div>
          ) : null}
          {drivingLicenseFile?.fileUrl ? (
            <div className="mt-2">
              <DocumentSummaryField
                onClick={() => openFile(drivingLicenseFile?.id)}
                title="Driving License"
                className="grid grid-cols-2"
              />
            </div>
          ) : null}
          {voterIdFile?.fileUrl ? (
            <div className="mt-2">
              <DocumentSummaryField
                onClick={() => openFile(voterIdFile?.id)}
                title="Voter ID"
                className="grid grid-cols-2"
              />
            </div>
          ) : null}
        </>
      )}
    </>
  );
};

const CorporateShareholderItem = ({
  corp,
  directorsData,
}: {
  corp: ICorporateShareholder;
  directorsData?: DirectorBase[];
}) => {
  const { data: businessEntity } = useBusinessEntity(corp.id);
  const { labelOf } = useJurisdictions();

  const entityEmail = businessEntity?.generalCorporationInfo.entityEmail;
  const taxId =
    businessEntity?.generalCorporationInfo.taxIds?.[0]?.value ||
    businessEntity?.generalCorporationInfo.uniqueIdentifierFromJurisdiction?.value;

  return (
    <AccordionItem
      className="AccordionItem rounded-md bg-bg-disabled-subtle"
      value={corp.id?.toString()}
      key={corp.id}
    >
      <AccordionTrigger className="w-full ">
        <div className="py-3 px-6 flex justify-between items-center w-full text-text-placeholder">
          <span className="text-base ">
            {getCorporationName(corp) || corp.companyLegalName}
          </span>
        </div>
      </AccordionTrigger>
      <AccordionContent className=" p-4 pt-0">
        <div className="p-6 flex flex-col gap-2 bg-bg-disabled-subtle">
          <SummaryFieldKS
            heading="Corporation Name"
            value={getCorporationName(corp) || corp.companyLegalName}
          />
          {entityEmail && (
            <SummaryFieldKS heading="Email" value={entityEmail} />
          )}
          <SummaryFieldKS
            heading="Entity Type"
            value={
              getPrintableCorporationType(
                corp.corporationType,
                corp.incorporationCountry
              ) || '-'
            }
          />
          <SummaryFieldKS
            heading="Jurisdiction"
            value={labelOf(corp.jurisdiction) || '-'}
          />
          <SummaryFieldKS
            heading="Country"
            value={
              CountryUtil.getCountryByCode(corp.incorporationCountry)?.name ||
              corp.incorporationCountry ||
              '-'
            }
          />
          {taxId && <SummaryFieldKS heading="Tax ID" value={taxId} />}
          <CorporateShareholderSummaryField
            corporationId={corp.id}
            directorsData={directorsData}
          />
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

const CorporateShareholdersSummary = ({
  corporateShareholders,
  directorsData,
}: TCorporateShareholdersSummaryProps) => {
  if (!corporateShareholders?.length) return null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3 rounded-sm">
        <Accordion
          className="AccordionRoot flex flex-col gap-3"
          type="single"
          collapsible
        >
          {corporateShareholders.map((corp) => (
            <CorporateShareholderItem
              key={corp.id}
              corp={corp}
              directorsData={directorsData}
            />
          ))}
        </Accordion>
      </div>
    </div>
  );
};

export default CorporateShareholdersSummary;
